import { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import { Box, Flex } from '@rebass/emotion';
import ProductVariant from '../ProductVariant';
import ProductVariantInfo from '../ProductVariantInfo';
import AddToCartButton from '../AddToCartButton';
import ProductUpgradeMessage from '../ProductUpgradeMessage';
import messages from '../ProductLayout/ProductLayout.messages';
import { PricingPage_content_pricing_pages_layout_item_pricing_pages_multi_layout_products_products_id as DirectusProduct } from '../../__generated__/PricingPage';
import { PricingPageShopProduct_magento2_products_items_Magento2_ConfigurableProduct as ConfigurableMagentoProduct } from '../../hook/__generated__/PricingPageShopProduct';
import useProductVariant from '../../hook/useProductVariant';

import { Wrapper, Title, Picture, Content } from './styles';
import useLocaleWithFallback from '../../../../hooks/useLocaleWithFallback';

interface ProductTileProps {
  directusProduct: DirectusProduct;
  magentoProduct: ConfigurableMagentoProduct;
  country?: string;
  isAuthenticated?: boolean;
}

const ConfigurableProductTile: FC<ProductTileProps> = ({
  directusProduct,
  magentoProduct,
  country,
  isAuthenticated,
}) => {
  const { variants, configurableOptions } = magentoProduct;

  const { selectedVariant, setSelectedVariant } = useProductVariant(variants);
  const { findTranslationByKeyWithFallback } = useLocaleWithFallback();

  const { title, translations } = directusProduct;
  const content = findTranslationByKeyWithFallback({
    translations,
    key: 'multiproduct_layout_content',
  });

  return (
    <Wrapper>
      <Picture
        data-testid="configurable-product-tile-image"
        src={selectedVariant?.product?.image?.url ?? undefined}
        alt={selectedVariant?.product?.image?.label ?? undefined}
      />
      <Title data-testid="configurable-product-tile-title" as="h3">
        {title}
      </Title>

      {content && (
        <Content dangerouslySetInnerHTML={{ __html: content }}></Content>
      )}

      <Flex mb={3}>
        <Box flexGrow={1}>
          <ProductVariant
            variants={variants || []}
            selectedVariant={selectedVariant}
            configurableOptions={configurableOptions || []}
            onChange={setSelectedVariant}
          />
        </Box>
      </Flex>

      <Box flexGrow={1}>
        {selectedVariant && (
          <ProductVariantInfo variant={selectedVariant} countryCode={country} />
        )}
      </Box>

      <Box mb={3}>
        <ProductUpgradeMessage
          isAuthenticated={isAuthenticated}
          selectedVariant={selectedVariant}
          selectVariant={setSelectedVariant}
          variants={variants}
        />
      </Box>

      <AddToCartButton product={selectedVariant?.product} quantity={1}>
        <FormattedMessage {...messages.addToCart} />
      </AddToCartButton>
    </Wrapper>
  );
};

export default ConfigurableProductTile;
